<template>
  <div class="relative">
    <div class="relative flex items-center">
      <!-- Input de recherche -->
      <input v-if="!selectedEntity" v-model="searchQuery" type="text"
        class="w-full p-2 border border-gray-300 rounded-md mb-0 no-margin-bottom"
        :class="{ 'bg-gray-100 cursor-not-allowed': isModelMode }" :placeholder="getPlaceholder" :disabled="isModelMode"
        @input="handleSearch" @focus="handleFocus" @blur="handleBlur" />

      <!-- Affichage de l'entité sélectionnée -->
      <div v-if="selectedEntity"
        class="w-full p-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-between">
        <div class="flex-1">
          <div class="font-medium text-gray-900">{{ selectedEntity.appellation || 'Entité sans nom' }}</div>
          <div class="text-sm text-gray-600">
            <span v-if="selectedEntity.type">{{ selectedEntity.type }}</span>
            <span v-if="selectedEntity.type && selectedEntity.format"> - </span>
            <span v-if="selectedEntity.format">{{ selectedEntity.format }}</span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <!-- Badge public si applicable -->
          <span v-if="selectedEntity.is_public"
            class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
            <GlobeIcon class="h-3 w-3 mr-1" />
            Public
          </span>

          <!-- Bouton d'édition (seulement pour mes entités) -->
          <button v-if="canEditSelectedEntity && !readOnlyConfig?.isReadOnly" title="Modifier l'entité"
            class="p-2 text-blue-500 hover:text-blue-700" @click="editEntity">
            <PencilIcon class="h-4 w-4" />
          </button>

          <!-- Bouton de réinitialisation -->
          <button v-if="!readOnlyConfig?.isReadOnly" title="Réinitialiser" class="p-2 text-red-500 hover:text-red-700"
            @click="resetAll">
            <XIcon class="h-4 w-4" />
          </button>
        </div>
      </div>

      <!-- Bouton de réinitialisation pour le mode recherche -->
      <button v-if="(searchQuery || showUserEntities) && !selectedEntity && !readOnlyConfig?.isReadOnly"
        title="Réinitialiser"
        class="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-red-500 hover:text-red-700"
        @click="resetAll">
        <XIcon class="h-4 w-4" />
      </button>
    </div>

    <!-- Dropdown avec sections séparées -->
    <div v-if="showUserEntities && !selectedEntity && !isCreationOnlyMode"
      class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">
      <!-- Bouton créer -->
      <div v-if="!readOnlyConfig?.isReadOnly" title="Créer une nouvelle entité numérique"
        class="p-3 hover:bg-gray-100 cursor-pointer text-blue-600 border-b border-gray-100 flex items-center gap-2"
        @click="handleCreateNewEntity">
        <PlusIcon class="h-4 w-4" />
        <span>Créer une nouvelle entité numérique</span>
      </div>

      <!-- Section : Mes entités -->
      <div v-if="myEntities.length > 0" class="section">
        <div class="section-header p-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
          <UserIcon class="h-4 w-4 text-blue-600" />
          <span class="text-sm font-medium text-blue-900">Mes entités ({{ myEntities.length }})</span>
        </div>
        <div v-for="entity in myEntities" :key="entity.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-blue-500"
          @click="selectUserEntity(entity)">
          <div class="flex-1">
            <div class="font-medium text-gray-900">
              {{ entity.appellation || 'Entité sans nom' }}
            </div>
            <div class="text-sm text-gray-600">
              <span v-if="entity.type">{{ entity.type }}</span>
              <span v-if="entity.type && entity.format"> - </span>
              <span v-if="entity.format">{{ entity.format }}</span>
              <span v-if="!entity.type && !entity.format">
                Pas d'information disponible
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 ml-4">
            <button title="Utiliser comme modèle"
              class="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
              @click.stop="useAsTemplate(entity)">
              Modèle
            </button>
          </div>
        </div>
      </div>

      <!-- Section : Entités publiques -->
      <div v-if="publicEntities.length > 0" class="section">
        <div class="section-header p-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
          <GlobeIcon class="h-4 w-4 text-emerald-600" />
          <span class="text-sm font-medium text-emerald-900">Entités publiques ({{ publicEntities.length }})</span>
        </div>
        <div v-for="entity in publicEntities" :key="entity.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-emerald-500"
          @click="selectUserEntity(entity)">
          <div class="flex-1">
            <div class="font-medium text-gray-900 flex items-center gap-2">
              {{ entity.appellation || 'Entité sans nom' }}
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                <GlobeIcon class="h-3 w-3 mr-1" />
                Public
              </span>
            </div>
            <div class="text-sm text-gray-600">
              <span v-if="entity.type">{{ entity.type }}</span>
              <span v-if="entity.type && entity.format"> - </span>
              <span v-if="entity.format">{{ entity.format }}</span>
              <span v-if="!entity.type && !entity.format">
                Pas d'information disponible
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 ml-4">
            <button title="Utiliser comme modèle"
              class="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
              @click.stop="useAsTemplate(entity)">
              Modèle
            </button>
          </div>
        </div>
      </div>

      <!-- Message si aucune entité -->
      <div v-if="myEntities.length === 0 && publicEntities.length === 0" class="p-4 text-center text-gray-500">
        <div class="flex flex-col items-center gap-2">
          <HardDriveIcon class="h-8 w-8 text-gray-400" />
          <span>Aucune entité numérique trouvée</span>
          <span class="text-xs">Créez-en une nouvelle pour commencer</span>
        </div>
      </div>
    </div>

    <!-- Dropdown spécial pour le mode creation-only -->
    <div v-if="showUserEntities && !selectedEntity && isCreationOnlyMode"
      class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">
      <!-- Bouton créer -->
      <div v-if="!readOnlyConfig?.isReadOnly" title="Créer une nouvelle entité numérique"
        class="p-3 hover:bg-gray-100 cursor-pointer text-blue-600 border-b border-gray-100 flex items-center gap-2"
        @click="handleCreateNewEntity">
        <PlusIcon class="h-4 w-4" />
        <span>Créer une nouvelle entité numérique</span>
      </div>

      <!-- Section : Mes entités (comme modèles seulement) -->
      <div v-if="myEntities.length > 0" class="section">
        <div class="section-header p-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
          <UserIcon class="h-4 w-4 text-amber-600" />
          <span class="text-sm font-medium text-amber-900">Utiliser comme modèle ({{ myEntities.length }})</span>
        </div>
        <div v-for="entity in myEntities" :key="entity.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-amber-500"
          @click="useAsTemplate(entity)">
          <div class="flex-1">
            <div class="font-medium text-gray-900">
              {{ entity.appellation || 'Entité sans nom' }}
            </div>
            <div class="text-sm text-gray-600">
              <span v-if="entity.type">{{ entity.type }}</span>
              <span v-if="entity.type && entity.format"> - </span>
              <span v-if="entity.format">{{ entity.format }}</span>
              <span v-if="!entity.type && !entity.format">
                Pas d'information disponible
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 ml-4">
            <span class="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded">
              Modèle
            </span>
          </div>
        </div>
      </div>

      <!-- Section : Entités publiques (comme modèles seulement) -->
      <div v-if="publicEntities.length > 0" class="section">
        <div class="section-header p-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
          <GlobeIcon class="h-4 w-4 text-emerald-600" />
          <span class="text-sm font-medium text-emerald-900">Modèles publics ({{ publicEntities.length }})</span>
        </div>
        <div v-for="entity in publicEntities" :key="entity.uuid"
          class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-emerald-500"
          @click="useAsTemplate(entity)">
          <div class="flex-1">
            <div class="font-medium text-gray-900 flex items-center gap-2">
              {{ entity.appellation || 'Entité sans nom' }}
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                <GlobeIcon class="h-3 w-3 mr-1" />
                Public
              </span>
            </div>
            <div class="text-sm text-gray-600">
              <span v-if="entity.type">{{ entity.type }}</span>
              <span v-if="entity.type && entity.format"> - </span>
              <span v-if="entity.format">{{ entity.format }}</span>
              <span v-if="!entity.type && !entity.format">
                Pas d'information disponible
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 ml-4">
            <span class="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded">
              Modèle
            </span>
          </div>
        </div>
      </div>

      <!-- Message si aucune entité -->
      <div v-if="myEntities.length === 0 && publicEntities.length === 0" class="p-4 text-center text-gray-500">
        <div class="flex flex-col items-center gap-2">
          <HardDriveIcon class="h-8 w-8 text-gray-400" />
          <span>Aucune entité numérique trouvée</span>
          <span class="text-xs">Créez-en une nouvelle pour commencer</span>
        </div>
      </div>
    </div>



    <!-- Modal DigitalEntityCreator -->
    <DigitalEntityCreator :show="showCreator" :mode="creatorMode" :initial-data="selectedEntityForEdit"
      :user-id="userId" :me-appellation="meAppellation" :me-identifier="meIdentifier"
      :creation-only-mode="creationOnlyMode" @update:show="showCreator = $event"
      @digital-entity-created="handleEntityCreated" @digital-entity-updated="handleEntityUpdated" />
    
    <!-- Modal de confirmation pour la réinitialisation -->
    <ModalAreYouSure 
      v-model="showResetConfirmModal"
      title="Réinitialiser l'entité numérique"
      message="Êtes-vous sûr de vouloir réinitialiser cette entité numérique ? Cette action ne pourra pas être annulée."
      confirm-text="Réinitialiser"
      cancel-text="Annuler"
      @confirm="confirmReset"
      @cancel="cancelReset"
    />
  </div>
</template>

<script lang="ts">
import { useDigitalEntities } from '@/composables/useAssets';
import { GlobeIcon, HardDriveIcon, PencilIcon, PlusIcon, UserIcon, XIcon } from 'lucide-vue-next';
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import type { DigitalEntity, DigitalEntityCreate } from '../../api/types/entities/digitalEntity/types';
import DigitalEntityCreator from '../creators/DigitalEntityCreator.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

export default defineComponent({
  name: 'DigitalEntitySelector',
  components: {
    XIcon,
    PencilIcon,
    UserIcon,
    GlobeIcon,
    PlusIcon,
    HardDriveIcon,
    DigitalEntityCreator,
    ModalAreYouSure,
  },
  props: {
    userId: {
      type: String,
      required: true
    },
    initialEntityUuid: {
      type: String,
      required: false,
      default: ''
    },
    digitalEntity: {
      type: Object,
      required: false,
      default: null
    },
    newEntityMode: {
      type: Boolean,
      required: false,
      default: false
    },
    creationOnlyMode: {
      type: Boolean,
      required: false,
      default: false
    },
    isModelMode: {
      type: Boolean,
      required: false,
      default: false
    },
    hasCreatedNewEntity: {
      type: Boolean,
      required: false,
      default: false
    },
    meAppellation: {
      type: String,
      required: false,
      default: ''
    },
    meIdentifier: {
      type: String,
      required: false,
      default: ''
    },
    // Nouveaux props du système de cache
    assetsCacheStore: {
      type: Object,
      default: null
    },
    canEditAsset: {
      type: Function,
      default: null
    },
    isPublicAsset: {
      type: Function,
      default: null
    },
    isMyAsset: {
      type: Function,
      default: null
    },
    readOnlyConfig: {
      type: Object,
      required: false,
      default: () => ({ isReadOnly: false, allowNavigation: true, showEditHints: true })
    }
  },
  emits: ['entity-selected', 'entity-reset', 'entity-created'],

  setup(props, { emit }) {
    // Utilisation du composable pour le cache avec filtre 'accessible' pour avoir toutes les entités
    const {
      filteredAssets: allEntities,
      isLoading,
      addAsset,
      updateAsset,
      canEditAsset: composableCanEdit,
      isMyAsset: composableIsMyAsset,
      isPublicAsset: composableIsPublicAsset
    } = useDigitalEntities({ defaultFilter: 'accessible' });

    // État local
    const searchQuery = ref('');
    const showUserEntities = ref(false);
    const selectedEntity = ref<DigitalEntity | null>(null);
    const modeEdit = ref(false);

    // Variables pour le modal
    const showCreator = ref(false);
    const creatorMode = ref<'create' | 'edit' | 'duplicate'>('create');
    const selectedEntityForEdit = ref<DigitalEntity | undefined>(undefined);

    // Variable pour le modal de confirmation de réinitialisation
    const showResetConfirmModal = ref(false);

    const entityData = reactive<DigitalEntityCreate>({
      user_id: props.userId,
      appellation: '',
      type: '',
      format: '',
      url: '',
      version: '',
      copyright: '',
      de_link: '',
      file_spec: '',
      is_public: false
    });

    // Fonction d'initialisation de l'entité sélectionnée
    const initializeSelectedEntity = (): void => {
      // Si on a une entité directement passée en prop
      if (props.digitalEntity && typeof props.digitalEntity === 'object' && 'uuid' in props.digitalEntity) {
        selectedEntity.value = props.digitalEntity as DigitalEntity;
        searchQuery.value = props.digitalEntity.appellation || 'Entité sans nom';
        return;
      }

      // Si on a un UUID, chercher l'entité dans la liste
      if (props.initialEntityUuid && allEntities.value.length > 0) {
        const foundEntity = allEntities.value.find(entity => entity.uuid === props.initialEntityUuid);
        if (foundEntity) {
          selectedEntity.value = foundEntity;
          searchQuery.value = foundEntity.appellation || 'Entité sans nom';
        }
      }
    };

    // Watcher pour initialiser quand les entités sont chargées
    watch([() => allEntities.value, () => props.initialEntityUuid, () => props.digitalEntity], () => {
      initializeSelectedEntity();
    }, { immediate: true });

    // Computed pour séparer mes entités des publiques avec recherche
    const myEntities = computed((): DigitalEntity[] => {
      const filtered = allEntities.value.filter(entity => {
        // Utiliser les helpers du composable ou ceux passés en props
        const isMyAssetFn = props.isMyAsset || composableIsMyAsset;
        return isMyAssetFn(entity);
      });

      if (!searchQuery.value) return filtered;

      return filtered.filter(entity => {
        const searchableFields = ['appellation', 'identifier', 'type', 'format'];
        return searchableFields.some(field => {
          const value = entity[field as keyof DigitalEntity];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        });
      });
    });

    const publicEntities = computed((): DigitalEntity[] => {
      const filtered = allEntities.value.filter(entity => {
        // Utiliser les helpers du composable ou ceux passés en props
        const isPublicAssetFn = props.isPublicAsset || composableIsPublicAsset;
        return isPublicAssetFn(entity);
      });

      if (!searchQuery.value) return filtered;

      return filtered.filter(entity => {
        const searchableFields = ['appellation', 'identifier', 'type', 'format'];
        return searchableFields.some(field => {
          const value = entity[field as keyof DigitalEntity];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        });
      });
    });

    // Helper pour vérifier si on peut éditer l'entité sélectionnée
    const canEditSelectedEntity = computed((): boolean => {
      if (!selectedEntity.value) return false;

      // Utiliser les helpers du composable ou ceux passés en props
      const canEditFn = props.canEditAsset || composableCanEdit;
      return canEditFn(selectedEntity.value);
    });

    // Computed pour le mode creation-only
    const isCreationOnlyMode = computed((): boolean => {
      return props.creationOnlyMode;
    });

    const handleFocus = (): void => {
      if (props.isModelMode || selectedEntity.value) return;
      showUserEntities.value = true;
    };

    const handleSearch = (): void => {
      if (props.isModelMode || selectedEntity.value) return;
      showUserEntities.value = true;
    };

    const selectUserEntity = (entity: DigitalEntity): void => {
      // En mode creation-only, on ne peut pas sélectionner directement une entité
      if (props.creationOnlyMode) {
        // Rediriger vers useAsTemplate au lieu de sélectionner
        useAsTemplate(entity);
        return;
      }

      selectedEntity.value = entity;
      searchQuery.value = entity.appellation || 'Entité sans nom';
      emit('entity-selected', { uuid: entity.uuid, entity });
      showUserEntities.value = false;
    };

    const resetSearch = (): void => {
      selectedEntity.value = null;
      searchQuery.value = '';
      showUserEntities.value = false;
      modeEdit.value = false;
    };

    const resetAll = (): void => {
      showResetConfirmModal.value = true;
    };

    const confirmReset = (): void => {
      resetSearch();
      emit('entity-reset');
      showResetConfirmModal.value = false;
    };

    const cancelReset = (): void => {
      showResetConfirmModal.value = false;
    };

    const openCreator = (): void => {
      creatorMode.value = 'create';
      selectedEntityForEdit.value = undefined;
      showCreator.value = true;
      showUserEntities.value = false;
    };

    const editEntity = (): void => {
      if (!selectedEntity.value || !canEditSelectedEntity.value) {
        return;
      }
      creatorMode.value = 'edit';
      selectedEntityForEdit.value = selectedEntity.value;
      showCreator.value = true;
      showUserEntities.value = false;
    };

    const handleEntityCreated = (entity: DigitalEntity): void => {
      // Ajouter au cache
      addAsset(entity);

      // Sélectionner l'entité créée (même en mode creation-only)
      selectedEntity.value = entity;
      searchQuery.value = entity.appellation || 'Entité sans nom';
      emit('entity-selected', { uuid: entity.uuid, entity });

      // Émettre aussi entity-created pour compatibilité
      emit('entity-created', { uuid: entity.uuid, entity });

      // Fermer le modal
      showCreator.value = false;
    };

    const handleEntityUpdated = (entity: DigitalEntity): void => {
      // Mettre à jour le cache
      updateAsset(entity);

      // Mettre à jour l'entité sélectionnée
      selectedEntity.value = entity;

      // Fermer le modal
      showCreator.value = false;
    };

    const useAsTemplate = (entity: DigitalEntity): void => {
      creatorMode.value = 'duplicate';
      selectedEntityForEdit.value = entity;
      showCreator.value = true;
      showUserEntities.value = false;
    };

    const handleCreateNewEntity = (): void => {
      openCreator();
    };

    const handleBlur = (): void => {
      // On utilise setTimeout pour permettre aux clics sur la liste d'être traités avant
      setTimeout(() => {
        showUserEntities.value = false;
      }, 200);
    };



    // useAsTemplate est déjà défini plus haut

    const getPlaceholder = computed((): string => {
      if (props.isModelMode) {
        return selectedEntity.value ? selectedEntity.value.appellation : 'Mode modèle activé, sélection impossible';
      }
      else if (selectedEntity.value) {
        return selectedEntity.value.appellation || 'Entité sans nom';
      }
      return 'Rechercher une entité numérique...';
    });

    return {
      searchQuery,
      showUserEntities,
      selectedEntity,
      entityData,
      modeEdit,
      myEntities,
      publicEntities,
      canEditSelectedEntity,
      isCreationOnlyMode,
      isLoading,
      handleFocus,
      handleSearch,
      handleBlur,
      selectUserEntity,
      resetSearch,
      resetAll,
      confirmReset,
      cancelReset,
      handleCreateNewEntity,
      openCreator,
      editEntity,
      handleEntityCreated,
      handleEntityUpdated,
      useAsTemplate,
      showCreator,
      creatorMode,
      selectedEntityForEdit,
      getPlaceholder,
      showResetConfirmModal
    };
  }
});
</script>

<style scoped>
.border-l-3 {
  border-left-width: 3px;
}

.section-header {
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>